/*
 * Copyright (c) 2018 DV Bern AG, Switzerland
 *
 * Das vorliegende Dokument, einschliesslich aller seiner Teile, ist urheberrechtlich
 * geschuetzt. Jede Verwertung ist ohne Zustimmung der DV Bern AG unzulaessig. Dies gilt
 * insbesondere fuer Vervielfaeltigungen, die Einspeicherung und Verarbeitung in
 * elektronischer Form. Wird das Dokument einem Kunden im Rahmen der Projektarbeit zur
 * Ansicht uebergeben ist jede weitere Verteilung durch den Kunden an Dritte untersagt.
 *
 */

import {IComponentOptions, IFormController, ILogService, IPromise} from 'angular';
import {Moment} from 'moment';
import ErrorService from '../../../core/errors/service/ErrorService';
import BatchJobRS from '../../../core/service/batchRS.rest';
import {DownloadRS} from '../../../core/service/downloadRS.rest';
import {ReportAsyncRS} from '../../../core/service/reportAsyncRS.rest';
import RoleUtilService from '../../../core/service/RoleUtilService';
import {
    displayAuszahlungsDatumMap,
    displayEreignisDatumMap,
    displayGesuchsDatumMap,
    getTSReportTypeValuesWhitoutKanton,
    TSReportType
} from '../../../models/enums/TSReportType';
import {TSStatistikParameterType} from '../../../models/enums/TSStatistikParameterType';
import TSBatchJobInformation from '../../../models/TSBatchJobInformation';
import TSDownloadFile from '../../../models/TSDownloadFile';
import TSReportTypeDTO from '../../../models/TSReportTypeDTO';
import TSReportTypeParams from '../../../models/TSReportTypeParams';
import TSWorkJob from '../../../models/TSWorkJob';
import DateUtil from '../../../utils/DateUtil';
import DVBernUtil from '../../../utils/DVBernUtil';
import AuthServiceRS from '../../../authentication/service/AuthServiceRS.rest';
import {TSRole} from '../../../models/enums/TSRole';
import ITranslateService = angular.translate.ITranslateService;

let template = require('./statistikView.html');

export class StatistikViewComponentConfig implements IComponentOptions {
    transclude: boolean = false;
    template: string = template;
    controller: any = StatistikViewController;
    controllerAs: string = 'vm';
}

export class StatistikViewController {

    public static $inject: string[] = [

        'ReportAsyncRS',
        'DownloadRS',
        'BatchJobRS',
        'ErrorService',
        '$log',
        '$interval',
        'RoleUtilService',
        '$translate',
        'AuthServiceRS'
    ];
    public readonly TSStatistikParameterType = TSStatistikParameterType;
    public allJobs: Array<TSBatchJobInformation>;
    public userjobs: Array<TSWorkJob>;
    private polling: IPromise<any>;
    public reportTypeDTO: TSReportTypeDTO = new TSReportTypeDTO();
    public form: IFormController;

    /* @ngInject */
    constructor(
        private readonly reportAsyncRS: ReportAsyncRS,
        private readonly downloadRS: DownloadRS,
        private readonly batchJobRS: BatchJobRS,
        private readonly errorService: ErrorService,
        private readonly $log: ILogService,
        private readonly $interval: angular.IIntervalService,
        private roleUtilService: RoleUtilService,
        private $translate: ITranslateService,
        private authServiceRS: AuthServiceRS
    ) {
    }

    $onInit() {

        this.refreshUserJobs();
        this.initBatchJobPolling();

        // Inspektor only can generate Auszahlung for Kanton
        if (!this.authServiceRS.isRole(TSRole.FONDSSUISSE_ADMIN)) {
            this.reportTypeDTO.reportType = TSReportType.AUSZAHLUNG_KANTON;
        }
    }

    public $onDestroy(): void {
        if (this.polling) {
            this.$interval.cancel(this.polling);
            this.$log.debug('canceld job polling');
        }
    }

    private initBatchJobPolling(): void {
        // check all 8 seconds for the state
        const delay = 12000;
        this.polling = this.$interval(() => this.refreshUserJobs(), delay);

    }

    private refreshUserJobs(): void {
        this.batchJobRS.getBatchJobsOfUser().then((response: TSWorkJob[]) => {
            this.userjobs = response;
        });
    }

    public generateStatistik(type?: TSStatistikParameterType): void {
        if (!this.form.$valid) {
            return;
        }
        this.$log.debug('Validated Form: ' + this.form.$name);

        if ((this.reportTypeDTO.auszahlungsDatumVon && this.reportTypeDTO.auszahlungsDatumBis) &&
            (this.reportTypeDTO.auszahlungsDatumBis < this.reportTypeDTO.auszahlungsDatumVon)) {
            this.errorService.addMesageAsError('ERROR_DATE_BEFORE_TO');
            throw new Error(`to cannot be behind from ${this.reportTypeDTO.auszahlungsDatumVon} > ${this.reportTypeDTO.auszahlungsDatumBis}`);
        }
        if ((this.reportTypeDTO.ereignisDatumVon && this.reportTypeDTO.ereignisDatumBis) &&
            (this.reportTypeDTO.ereignisDatumBis < this.reportTypeDTO.ereignisDatumVon)) {
            this.errorService.addMesageAsError('ERROR_DATE_BEFORE_TO');
            throw new Error(`to cannot be behind from ${this.reportTypeDTO.ereignisDatumVon} > ${this.reportTypeDTO.ereignisDatumBis}`);
        }
        if ((this.reportTypeDTO.gesuchDatumVon && this.reportTypeDTO.gesuchDatumBis) &&
            (this.reportTypeDTO.gesuchDatumBis < this.reportTypeDTO.gesuchDatumVon)) {
            this.errorService.addMesageAsError('ERROR_DATE_BEFORE_TO');
            throw new Error(`to cannot be behind from ${this.reportTypeDTO.gesuchDatumVon} > ${this.reportTypeDTO.gesuchDatumBis}`);
        }

        let reportTypeDTOtoSend = new TSReportTypeDTO();
        reportTypeDTOtoSend.reportType = this.reportTypeDTO.reportType;

        switch (reportTypeDTOtoSend.reportType) {
            case TSReportType.SCHADEN:
            case TSReportType.ALLE:
                reportTypeDTOtoSend.ereignisDatumVon = this.reportTypeDTO.ereignisDatumVon;
                reportTypeDTOtoSend.ereignisDatumBis = this.reportTypeDTO.ereignisDatumBis;
                reportTypeDTOtoSend.gesuchDatumVon = this.reportTypeDTO.gesuchDatumVon;
                reportTypeDTOtoSend.gesuchDatumBis = this.reportTypeDTO.gesuchDatumBis;
                break;

            case TSReportType.AUSZAHLUNG:
            case TSReportType.AUSZAHLUNG_KANTON:
                reportTypeDTOtoSend.auszahlungsDatumVon = this.reportTypeDTO.auszahlungsDatumVon;
                reportTypeDTOtoSend.auszahlungsDatumBis = this.reportTypeDTO.auszahlungsDatumBis;
                reportTypeDTOtoSend.ereignisDatumVon = this.reportTypeDTO.ereignisDatumVon;
                reportTypeDTOtoSend.ereignisDatumBis = this.reportTypeDTO.ereignisDatumBis;
                reportTypeDTOtoSend.gesuchDatumVon = this.reportTypeDTO.gesuchDatumVon;
                reportTypeDTOtoSend.gesuchDatumBis = this.reportTypeDTO.gesuchDatumBis;
                break;

        }

        switch (type) {
            case TSStatistikParameterType.TEST:
                this.reportAsyncRS.getReport(reportTypeDTOtoSend)
                    .then((batchExecutionId: string) => {
                        this.informReportGenerationStarted(batchExecutionId);
                    });
                this.form.$setPristine();
                return;
            default:
                throw new Error(`unknown TSStatistikParameterType: ${type}`);
        }
    }

    public downloadClicked(row: TSWorkJob): void {
        if (DVBernUtil.isNullOrUndefined(row) || DVBernUtil.isNullOrUndefined(row.execution)) {
            return;
        }

        if (DVBernUtil.isNullOrUndefined(row.execution.batchStatus) || row.execution.batchStatus !== 'COMPLETED') {
            this.$log.info('batch-job is not yet finnished');
            return;
        }

        this.$log.debug('download dokument with id ' + row.serfDokumentId);
        let win: Window = this.downloadRS.prepareDownloadWindow();

        this.downloadRS.getAccessTokenDocumentById(row.serfDokumentId).then((downloadFile: TSDownloadFile) => {
            this.$log.debug('accessToken: ' + downloadFile.accessToken);
            this.downloadRS.startDownload(downloadFile.accessToken, 'report.xlsx', false, win);
        });
    }

    private informReportGenerationStarted(batchExecutionId: string): void {
        this.$log.debug('executionID: ' + batchExecutionId);
        this.errorService.addMesageAsInfo('STARTED_GENERATION');
        this.refreshUserJobs();
    }

    /**
     * helper methode die es dem Admin erlaubt alle jobs zu sehen
     */
    public showAllJobs(): void {
        this.batchJobRS.getAllJobs().then((result: TSWorkJob[]) => {
            let res: TSBatchJobInformation[] = [];
            res = res.concat(result.map(value => {
                return value.execution || undefined;
            }));
            this.allJobs = res;
        });
    }

    public getTSReportTypeValues(): Array<TSReportType> {
        return getTSReportTypeValuesWhitoutKanton();
    }

    public displayAuszahlungsDatum(): boolean | undefined {
        return displayAuszahlungsDatumMap.get(this.reportTypeDTO.reportType);
    }

    public displayEreignisDatum(): boolean | undefined {
        return displayEreignisDatumMap.get(this.reportTypeDTO.reportType);
    }

    public displayGesuchsDatum(): boolean | undefined {

        return displayGesuchsDatumMap.get(this.reportTypeDTO.reportType);
    }

    public getFormattedDate(s: string) {
        let mom: Moment | undefined = DateUtil.localDateToMoment(s.substring(0, 10));
        if (mom) {
            return DateUtil.momentToLocalDateFormat(mom, 'DD.MM.YYYY');
        }
        return '';
    }

    public isDate(params: TSReportTypeParams, dateType: string) {
        switch (dateType) {
            case 'AD':
                return !!params.ADV && !!params.ADB;
            case 'ED':
                return !!params.EDV && !!params.EDB;
            case 'GD':
                return !!params.GDV && !!params.GDB;
            default:
                return false;
        }
    }

    public getParams(params: TSReportTypeParams): string {
        let returnString = '';
        let separator = ' - ';
        const endOfLine = '\n';

        if (this.isDate(params, 'AD')) {
            returnString += this.$translate.instant('DATUM_AUSZAHLUNG_PARAM');
            returnString += this.getFormattedDate(params.ADV);
            returnString += separator;
            returnString += this.getFormattedDate(params.ADB);
            returnString += endOfLine;
        }

        if (this.isDate(params, 'ED')) {
            returnString += this.$translate.instant('DATUM_EREIGNIS_PARAM');
            returnString += this.getFormattedDate(params.EDV);
            returnString += separator;
            returnString += this.getFormattedDate(params.EDB);
            returnString += endOfLine;
        }

        if (this.isDate(params, 'GD')) {
            returnString += this.$translate.instant('DATUM_GESUCH_PARAM');
            returnString += this.getFormattedDate(params.GDV);
            returnString += separator;
            returnString += this.getFormattedDate(params.GDB);
            returnString += endOfLine;
        }

        return returnString;
    }

}



// WEBPACK FOOTER //
// ./src/app/statistik/component/statistikView/statistikView.ts